import React, { useRef } from "react";
import MainLayout from "../../layout/main-layout";
import MetaTags from "../../utils/meta-tags";
import Grid from "../Grid/grid";
import Title from "../title";
import Process from "../process";
import ContactForm from "../contact-form/contact-form";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import defaultBanner from "../../Assets/image/services-pages-images/content management system/cms-banner.webp";

export interface BenefitItem {
  title: string;
  description: string;
}

export interface ProcessItem {
  step: number;
  title: string;
  icon: React.ReactNode;
  description: string;
}

export interface WhyChooseItem {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface ServicePageTemplateProps {
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  heroTitle: string;
  heroSubtitle: string;
  heroImage: string;
  benefitsTitle: string;
  benefitsHeading: string;
  benefitsList: BenefitItem[];
  benefitsBgImage?: string;
  processTitle: string;
  processDescription: string;
  processList: ProcessItem[];
  processImage: string;
  whyChooseTitle: string;
  whyChooseList: WhyChooseItem[];
  contactHeading: string;
  contactDescription: string;
  contactPurpose: string;
  contactFormTitle: string;
}

const ServicePageTemplate: React.FC<ServicePageTemplateProps> = ({
  metaTitle,
  metaDescription,
  canonicalUrl,
  heroTitle,
  heroSubtitle,
  heroImage,
  benefitsTitle,
  benefitsHeading,
  benefitsList,
  benefitsBgImage,
  processTitle,
  processDescription,
  processList,
  processImage,
  whyChooseTitle,
  whyChooseList,
  contactHeading,
  contactDescription,
  contactPurpose,
  contactFormTitle,
}) => {
  const contactRef = useRef<HTMLDivElement>(null);

  const handleScrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      <MetaTags
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalUrl}
      />
      <MainLayout>
        {/* Hero Section */}
        <section className="w-full min-h-screen h-full relative flex flex-col">
          <img
            src={heroImage}
            alt={heroTitle}
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 flex flex-col gap-5 items-start p-10 justify-center bg-black/50">
            <motion.h1
              className="text-white text-4xl font-light"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              {heroTitle}
            </motion.h1>
            <motion.p
              className="text-base text-white max-w-2xl font-light"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
            >
              {heroSubtitle}
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              onClick={handleScrollToContact}
              className="px-4 py-2 border-2 border-white rounded-full flex items-center gap-2 text-white group ease-in"
            >
              <ArrowRight className="group-hover:rotate-[360deg] duration-200 ease-in" />
              Connect Now
            </motion.button>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="w-full min-h-screen h-full relative flex flex-col overflow-hidden">
          <Grid size={100} direction="top-center" />
          <div className="p-2 lg:p-16 flex flex-col w-full items-center justify-center">
            <div className="z-10 flex justify-start items-start mb-12 w-full">
              <Title title={benefitsTitle} />
            </div>
            <h2 className="md:text-4xl text-xl font-medium md:font-light max-w-4xl text-center leading-relaxed">
              {benefitsHeading}
            </h2>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div
              className="w-full bg-cover block mt-5"
              style={{
                backgroundImage: `url(${benefitsBgImage || defaultBanner})`,
              }}
            >
              <div className="grid md:grid-cols-3 bg-black/60">
                {benefitsList.map((benefit, index) => (
                  <div key={index} className="p-12 border border-white flex flex-col gap-8 hover:bg-white/5 transition-colors duration-300">
                    <div className="flex flex-row items-center gap-4">
                      <p className="font-bold text-6xl text-white">0{index + 1}</p>
                      <h2 className="text-white text-2xl font-normal">{benefit.title}</h2>
                    </div>
                    <div>
                      <p className="text-white text-lg leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="w-full min-h-screen h-full relative flex flex-col overflow-hidden p-2 lg:p-16">
          <Grid size={100} direction="top-left" />
          <div className="z-10 flex justify-start items-start mb-12 w-full">
            <Title title={processTitle} />
          </div>
          <Process
            data={processList}
            img={processImage}
            title={heroTitle}
            description={processDescription}
          />
        </section>

        {/* Why Choose Us Section */}
        <section className="w-full h-auto relative flex flex-col overflow-hidden p-2 lg:p-16">
          <Grid size={100} direction="top-center" />
          <div className="z-10 flex justify-start items-start mb-12 w-full">
            <Title title={whyChooseTitle} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 z-10">
            {whyChooseList.map(({ id, title, description, icon }) => (
              <div
                key={id}
                className={`flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl border ${
                  whyChooseList.length % 2 !== 0 ? "last:md:col-span-2" : ""
                }`}
              >
                <div className="p-3 bg-primary/10 rounded-full text-primary flex-shrink-0">
                  {icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-1">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact/CTA Section */}
        <section ref={contactRef} className="w-full min-h-screen h-full relative flex flex-col overflow-hidden p-2 lg:p-16">
          <Grid size={100} direction="top-center" />
          <div className="z-10 flex justify-start items-start mb-12 w-full">
            <Title title="Get in touch" />
          </div>
          <div className="flex md:flex-row flex-col justify-center z-10">
            <div className="flex flex-col items-start justify-center gap-5 md:w-1/2">
              <h2 className="md:text-4xl text-xl font-medium max-w-4xl leading-snug">
                {contactHeading}
              </h2>
              <p className="text-gray-500 text-base leading-relaxed">
                {contactDescription}
              </p>
            </div>
            <div className="mt-5 flex items-center justify-center md:w-1/2 w-full">
              <ContactForm title={contactFormTitle} purpose={contactPurpose} />
            </div>
          </div>
        </section>
      </MainLayout>
    </div>
  );
};

export default ServicePageTemplate;
